/* ==========================================================================
   TRE EMME — CX home: slider, parallax/drift, shop the look
   Needs cx-core.js (window.CX).
   ========================================================================== */
(() => {
  'use strict';
  if (!window.CX) return;
  const { $, $$, reduceMotion } = window.CX;

  /* Slider — fade between slides, autoplay with a progress ring, pause on hover/focus/offscreen */
  $$('[data-cx-slider]').forEach((slider) => {
    const slides = $$('[data-cx-slide]', slider);
    if (slides.length < 2) { slides.forEach((s) => s.classList.add('is-active')); return; }
    const status = $('[data-cx-slider-status]', slider);
    const toggle = $('[data-cx-slider-toggle]', slider);
    const toggleLabel = $('[data-cx-slider-toggle-label]', slider);
    const interval = (Number(slider.dataset.interval) || 6) * 1000;
    let index = 0;
    let timer;
    let playing = slider.dataset.autoplay === 'true' && !reduceMotion.matches;
    let held = false;

    const syncVideo = () => slides.forEach((s, i) => {
      const v = $('video', s);
      if (!v) return;
      if (i === index) { const p = v.play(); p && p.catch(() => {}); } else v.pause();
    });

    // Restart the ring animation two frames later instead of forcing a layout (offsetWidth) on every slide change
    let ringRaf = 0;
    const restartRing = () => {
      slider.classList.remove('is-playing');
      cancelAnimationFrame(ringRaf);
      ringRaf = requestAnimationFrame(() => {
        ringRaf = requestAnimationFrame(() => { if (playing && !held) slider.classList.add('is-playing'); });
      });
    };
    // Decode the next slide's image ahead of time: decoding a full-width photo during the cross-fade cost a 60-90 ms frame
    const predecode = () => {
      const img = $('img', slides[(index + 1) % slides.length]);
      if (img && img.decode) img.decode().catch(() => {});
    };

    const schedule = () => {
      clearTimeout(timer);
      restartRing();
      if (playing && !held) timer = setTimeout(() => go(index + 1), interval);
    };

    const go = (n) => {
      index = (n + slides.length) % slides.length;
      slides.forEach((s, i) => {
        const on = i === index;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-hidden', String(!on));
        s.inert = !on;
      });
      if (status) status.textContent = `Slide ${index + 1} di ${slides.length}`;
      syncVideo();
      schedule();
      setTimeout(predecode, 1500); // after this cross-fade, well before the next one
    };

    const setPlaying = (on) => {
      playing = on;
      if (toggle) toggle.setAttribute('aria-pressed', String(!on));
      if (toggleLabel) toggleLabel.textContent = on ? 'Metti in pausa' : 'Riproduci';
      schedule();
    };

    $('[data-cx-slider-prev]', slider)?.addEventListener('click', () => go(index - 1));
    $('[data-cx-slider-next]', slider)?.addEventListener('click', () => go(index + 1));
    toggle?.addEventListener('click', () => setPlaying(!playing));

    const hold = (on) => { held = on; schedule(); };
    slider.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') hold(true); });
    slider.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') hold(false); });
    slider.addEventListener('focusin', () => hold(true));
    slider.addEventListener('focusout', (e) => { if (!slider.contains(e.relatedTarget)) hold(false); });

    // Swipe
    let x0 = null;
    slider.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse') x0 = e.clientX; });
    slider.addEventListener('pointerup', (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0;
      x0 = null;
      if (Math.abs(dx) > 40) go(index + (dx < 0 ? 1 : -1));
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) { held = false; schedule(); } else { held = true; clearTimeout(timer); slider.classList.remove('is-playing'); }
      }, { threshold: .25 }).observe(slider);
    }
    document.addEventListener('visibilitychange', () => { held = document.hidden; schedule(); });

    if (toggle) toggle.setAttribute('aria-pressed', String(!playing));
    if (toggleLabel) toggleLabel.textContent = playing ? 'Metti in pausa' : 'Riproduci';
    go(0);
  });

  /* Parallax & drift — one rAF loop for every element in view */
  const parallax = $$('[data-cx-parallax], [data-cx-drift]');
  // Parallax only with a mouse: on touch screens it fights the finger and feels like the page scrolls on its own
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  if (parallax.length && !reduceMotion.matches && finePointer) {
    const visible = new Set();
    const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? visible.add(e.target) : visible.delete(e.target)));
    }) : null;
    parallax.forEach((el) => (io ? io.observe(el) : visible.add(el)));
    let raf = 0;
    const paint = () => {
      raf = 0;
      const vh = window.innerHeight;
      visible.forEach((el) => {
        const r = el.getBoundingClientRect();
        const t = (r.top + r.height / 2 - vh / 2) / vh; // -1 … 1
        // "soft" = the subtle ±2% used on the home photos; plain = the ±6% of the full-bleed statement/newsletter
        if (el.hasAttribute('data-cx-parallax')) el.style.setProperty('--cx-par', `${(t * (el.dataset.cxParallax === 'soft' ? -2 : -6)).toFixed(2)}%`);
        else el.style.setProperty('--cx-drift', (t * -40).toFixed(1));
      });
    };
    window.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(paint); }, { passive: true });
    paint();
  }

  /* Shop the look — dots and products highlight each other */
  $$('[data-cx-look]').forEach((look) => {
    const dots = $$('[data-cx-look-dot]', look);
    const items = $$('[data-cx-look-item]', look);
    const mark = (n) => {
      dots.forEach((d) => d.classList.toggle('is-active', d.dataset.cxLookDot === n));
      items.forEach((i) => i.classList.toggle('is-active', i.dataset.cxLookItem === n));
    };
    dots.forEach((d) => {
      d.addEventListener('pointerenter', () => mark(d.dataset.cxLookDot));
      d.addEventListener('focus', () => mark(d.dataset.cxLookDot));
      d.addEventListener('click', () => {
        const item = items.find((i) => i.dataset.cxLookItem === d.dataset.cxLookDot);
        const link = item && $('.cx-card__titlelink', item);
        mark(d.dataset.cxLookDot);
        if (link && window.matchMedia('(max-width: 767px)').matches) item.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
        else link && link.focus();
      });
    });
    items.forEach((i) => i.addEventListener('pointerenter', () => mark(i.dataset.cxLookItem)));
    look.addEventListener('pointerleave', () => mark(null));
  });

  /* Instagram wall: when an Instafeed app block sits in the section, its posts replace the manual pictures
     (same scattered layout). The app renders asynchronously: wait up to 10 s, otherwise keep the manual pictures. */
  $$('[data-cx-insta]').forEach((section) => {
    const ticker = $('[data-cx-ticker]', section);
    const track = ticker && $('.cx-ticker__track', ticker);
    const source = $('[data-cx-insta-source]', section);
    const start = () => window.CX.initTicker && ticker && window.CX.initTicker(ticker);
    if (!source || !track) { start(); return; }
    const harvest = () => {
      const seen = new Set();
      const posts = [];
      // Instafeed: lazy images keep the real URL in data-instafeed-thumbnail / data-instafeed-src (posts open a modal, so they link to the profile)
      $$('.instafeed-new-layout-item[data-instafeed-thumbnail], [data-instafeed-thumbnail]', source).forEach((item) => {
        const src = item.getAttribute('data-instafeed-thumbnail');
        if (!src || seen.has(src)) return;
        seen.add(src);
        const img = $('img', item);
        posts.push({ href: section.dataset.profile || '#', src, alt: (img && img.alt) || '' });
      });
      if (posts.length >= 4) return posts;
      $$('img[data-instafeed-src]', source).forEach((img) => {
        const src = img.getAttribute('data-instafeed-src');
        if (!src || seen.has(src) || img.closest('.instafeed-hover-icon')) return;
        seen.add(src);
        posts.push({ href: section.dataset.profile || '#', src, alt: img.alt || '' });
      });
      if (posts.length >= 4) return posts;
      $$('a[href*="instagram.com"]', source).forEach((a) => {
        const img = $('img', a) || (a.closest('[class]') && $('img', a.closest('[class]')));
        const src = img && (img.currentSrc || img.src || img.dataset.src);
        if (!src || seen.has(src) || /^data:/.test(src)) return;
        seen.add(src);
        posts.push({ href: a.href, src, alt: img.alt || '' });
      });
      if (posts.length < 4) {
        // Some versions render images without a link per post
        $$('img', source).forEach((img) => {
          const src = img.currentSrc || img.src || img.dataset.src;
          if (!src || seen.has(src) || /^data:/.test(src) || img.width < 40) return;
          seen.add(src);
          posts.push({ href: section.dataset.profile || '#', src, alt: img.alt || '' });
        });
      }
      return posts;
    };
    const build = (posts) => {
      // whole 8-post patterns, so the loop seam is invisible
      const max = posts.length >= 16 ? 16 : 8;
      track.replaceChildren(...posts.slice(0, max).map((p, i) => {
        const li = document.createElement('li');
        li.className = `cx-social__pic cx-social__pic--${i % 8}`;
        const a = document.createElement('a');
        a.href = p.href; a.target = '_blank'; a.rel = 'noopener'; a.tabIndex = -1;
        const box = document.createElement('span');
        box.className = 'cx-social__img cx-media';
        const img = document.createElement('img');
        img.src = p.src; img.alt = p.alt; img.loading = 'lazy'; img.referrerPolicy = 'no-referrer'; img.className = 'cx-img is-loaded';
        box.appendChild(img);
        const n = document.createElement('span');
        n.className = 'cx-label cx-label--xs';
        n.textContent = `(${String(i + 1).padStart(2, '0')})`;
        a.append(box, n);
        li.appendChild(a);
        return li;
      }));
      section.classList.add('is-live');
    };
    let done = false;
    const finish = (posts) => {
      if (done) return;
      done = true;
      obs.disconnect();
      if (posts && posts.length >= 4) build(posts);
      start();
      // The app's widget keeps working off-screen (lazy images, layout): drop it once read; the editor keeps it
      if (!source.classList.contains('is-editing')) source.replaceChildren();
    };
    // The app mutates its widget many times while it loads: read it once per frame, not on every mutation
    // (each harvest queries the whole widget and reads img.width, a forced layout)
    let queued = 0;
    const obs = new MutationObserver(() => {
      if (queued) return;
      queued = requestAnimationFrame(() => { queued = 0; const p = harvest(); if (p.length >= 6) finish(p); });
    });
    obs.observe(source, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });
    const now = harvest();
    if (now.length >= 6) finish(now);
    setTimeout(() => finish(harvest()), 10000);
  });
})();
